import { notifications } from "@lib/notification/notification";

// Mirror of the per-file size, per-session count, and cumulative total-bytes limits
// (Django settings AGENT_DOCUMENT_MAX_*) and the extension allow-list
// (atinary_util.document_formats), enforced at upload time by the endpoint.
// Surfaced client-side so the user gets immediate feedback instead of a failed upload round-trip.
// Keep in sync — the backend stays authoritative.
export const MAX_FILE_BYTES = 4_500_000;
export const MAX_DOCS_PER_SESSION = 5;
export const MAX_TOTAL_BYTES = 22_500_000;
export const ACCEPTED_EXTENSIONS = ".pdf,.doc,.docx,.txt,.md";
// Human-readable form of ACCEPTED_EXTENSIONS for surfacing the allow-list to users (e.g. tooltips).
export const ACCEPTED_EXTENSIONS_LABEL = "PDF, DOC, DOCX, TXT, or MD";

export type FileAdmission = {
  accepted: File[];
  oversized: File[];
  droppedBySlots: number;
  droppedByTotalSize: number;
};

/**
 * Pure client-side admission of picked files against the session document caps, mirroring the
 * backend's per-file size, per-count, and cumulative total-size limits so the user gets immediate
 * feedback. The backend stays authoritative; this only sees the `existingCount` and `existingBytes`
 * the caller passes. Drop order: per-file size, then count slots, then cumulative total size.
 * Callers surface their own warnings from the returned counts.
 */
export function admitFiles(
  incoming: File[],
  { existingCount, existingBytes }: { existingCount: number; existingBytes: number }
): FileAdmission {
  const oversized = incoming.filter(f => f.size > MAX_FILE_BYTES);
  const sized = incoming.filter(f => f.size <= MAX_FILE_BYTES);

  const slotsLeft = MAX_DOCS_PER_SESSION - existingCount;
  const fitsCount = sized.slice(0, Math.max(0, slotsLeft));
  const droppedBySlots = sized.length - fitsCount.length;

  let runningBytes = existingBytes;
  const accepted: File[] = [];
  let droppedByTotalSize = 0;
  for (const file of fitsCount) {
    if (runningBytes + file.size <= MAX_TOTAL_BYTES) {
      accepted.push(file);
      runningBytes += file.size;
    } else {
      droppedByTotalSize++;
    }
  }

  return { accepted, oversized, droppedBySlots, droppedByTotalSize };
}

/**
 * Surface the standard cap/size warnings for an {@link admitFiles} result. Centralized so the
 * warning copy lives in one place wherever files are picked.
 */
export function notifyAdmissionWarnings({ oversized, droppedBySlots, droppedByTotalSize }: FileAdmission): void {
  if (oversized.length > 0) {
    notifications.warning(
      "File too large",
      `${oversized.map(f => f.name).join(", ")} exceeds the ${(MAX_FILE_BYTES / 1_000_000).toFixed(1)} MB per-file limit.`
    );
  }
  if (droppedBySlots > 0) {
    notifications.warning(
      "Document limit reached",
      `Only ${MAX_DOCS_PER_SESSION} documents can be attached at a time. ${droppedBySlots} file(s) were not added.`
    );
  }
  if (droppedByTotalSize > 0) {
    notifications.warning(
      "Attachment size limit reached",
      `${droppedByTotalSize} file(s) were not added because attachments would exceed the ${MAX_TOTAL_BYTES / 1_000_000} MB total size limit.`
    );
  }
}

/**
 * Build the multipart body for a document upload: one `files` part per document, matching the
 * endpoint's `files` field. The whole draft is sent in a single request so the backend commits the
 * documents atomically — all or none.
 */
export function buildUploadForm(files: File[]): FormData {
  const formData = new FormData();
  for (const file of files) {
    formData.append("files", file);
  }
  return formData;
}
